/**
=========================================================
* BASE: Learning Repository - v1.0.0
=========================================================

* Copyright 2022 Sean Dooley (https://www.dooley-dev.com)

Coded by www.dooley-dev.com

 =========================================================

* The above copyright notice shall be included in substantial portions of the Software.
*/

// React
import React from "react";
// redux hooks
import { useFieldArray, Controller } from "react-hook-form";
// BASE components
import BASESvgIcon from '../../../../../@BASE/core/BASESvgIcon';
// material-ui components
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Box from "@mui/system/Box";
import Button from "@mui/material/Button"
import Divider from '@mui/material/Divider';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TextField from "@mui/material/TextField";
import Timeline from '@mui/lab/Timeline';
import TimelineConnector from '@mui/lab/TimelineConnector';
import TimelineContent from '@mui/lab/TimelineContent';
import TimelineDot from '@mui/lab/TimelineDot';
import TimelineItem, { timelineItemClasses } from '@mui/lab/TimelineItem';
import TimelineSeparator from '@mui/lab/TimelineSeparator';
import Typography from "@mui/material/Typography";
// custom components
import CourseSelect from "./CourseSelect";
import NumberedHeading from "../../../../shared-components/forms/NumberedHeading";
import WYSIWYGEditor from "../../../../shared-components/WYSIWYGEditor";

export default function InstructionsArray({ control, register, setValue, getValues, errors }) {
  // setup field array
  const { fields, append, remove, move } = useFieldArray({
    control,
    name: 'instructions',
  });
  // handle course change related to direction
  const handleCourseChange = (index, newValue) => {
    fields[index].rtl = newValue;
  }

  return (
    <>
      <Timeline
        sx={{
          [`& .${timelineItemClasses.root}:before`]: {
            flex: 0,
            padding: 0,
          },
        }}
        className="px-0"
      >
        {fields.map((item, index) => {
          return (
            <TimelineItem key={item.id}>
              <TimelineSeparator>
                <TimelineDot color="secondary" />
                <TimelineConnector />
              </TimelineSeparator>
              <TimelineContent className="pr-0">
                <Accordion
                  defaultExpanded
                  className="bg-transparent shadow-none"
                >
                  <AccordionSummary
                    expandIcon={<ExpandMoreIcon />}
                    aria-controls={`${index}`}
                    id={`${index}`}
                    className="py-0 px-16 m-0 rounded bg-white shadow-sm"
                    sx={{
                      border: 'solid 1px',
                      borderColor: '#bdbdbd'
                    }}
                  >
                    <Typography className="m-0 font-semibold">
                      Instruction {index + 1}
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails className="p-0">
                    <Box
                      className="p-0"
                    >
                      <NumberedHeading number="1" title="Display Name" instructions="Enter a name for this instruction item." />
                      <Controller
                        control={control}
                        name={`instructions.${index}.title`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Display Name"
                              placeholder="Display Name"
                              variant="outlined"
                              fullWidth
                              error={!!errors.instructions?.[index]?.title}
                              helperText={errors?.instructions?.[index]?.title?.message}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="2" title="Label" instructions="Enter a class name for this instruction item." />
                      <Controller
                        control={control}
                        name={`instructions.${index}.label`}
                        render={({ field }) => (
                          <>
                            <TextField
                              {...field}
                              className="mt-16 mb-16"
                              label="Label"
                              placeholder="Label"
                              variant="outlined"
                              fullWidth
                              error={!!errors.instructions?.[index]?.label}
                              helperText={errors?.instructions?.[index]?.label?.message}
                            />
                          </>
                        )}
                      /> 

                      <Divider className="mt-16" />

                      <NumberedHeading number="3" title="Course" instructions="Select a course for this instruction item." />
                      <Controller
                        control={control}
                        name={`instructions.${index}.course`}
                        render={({ field }) => <CourseSelect handleCourseChange={handleCourseChange} index={index} errors={errors?.instructions?.[index]?.course} className="mt-16 mb-16" {...field} />}
                      />

                      <Divider className="mt-16" />

                      <NumberedHeading number="4" title="Instructions" instructions="Enter The instruction content for this instruction item." />
                      <Controller
                        control={control}
                        name={`instructions.${index}.content`}
                        render={({ field }) => <WYSIWYGEditor {...field} error={errors.instructions?.[index]?.content} />}
                      />
                    </Box>
                    <Box
                      className="flex items-center mt-32 p-0 border-t"
                      sx={{ backgroundColor: "background.default" }}
                    >
                      {index !== 0 &&
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index - 1)}
                        >
                          <BASESvgIcon size={20}>heroicons-solid:arrow-circle-up</BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Up
                          </span>
                        </Button>
                      }
                      {index !== fields.length - 1 &&
                        <Button
                          className="group flex items-center mt-2 -ml-4 mr-8 py-2 px-4 rounded cursor-pointer"
                          onClick={() => move(index, index + 1)}
                        >
                          <BASESvgIcon size={20}>heroicons-solid:arrow-circle-down</BASESvgIcon>
                          <span className="ml-8 font-medium text-secondary group-hover:underline">
                            Move Down
                          </span>
                        </Button>
                      }
                      <Button
                        className="group flex items-center mt-2 py-2 px-4 rounded cursor-pointer ml-auto"
                        onClick={() => remove(index)}
                      >
                        <BASESvgIcon size={20}>heroicons-solid:minus-circle</BASESvgIcon>
                        <span className="ml-8 font-medium text-secondary group-hover:underline">
                          Remove Instruction
                        </span>
                      </Button>
                    </Box>
                  </AccordionDetails>
                </Accordion>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
      <Box className="flex items-center mt-0 p-0">
        <Button
          className="group flex items-center mt-2 -ml-4 py-2 px-4 rounded cursor-pointer"
          onClick={() => append({ 
            title: '',
            label: '',
            course: 'Default',
            content: '',
          })}
        >
          <BASESvgIcon size={20}>heroicons-solid:plus-circle</BASESvgIcon>
          <span className="ml-8 font-medium text-secondary group-hover:underline">
            Add Instruction
          </span>
        </Button>
      </Box>
    </>
  );
}
